import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Search, Download, Check } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { exportCSV } from "../../lib/csv";
import { cn } from "../../lib/utils";

export default function AdminParticipants() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { tickets, getActivity, getSlot, setCheckIn } = useApp();
  const activity = getActivity(slug);
  const [q, setQ] = useState("");
  const [slotFilter, setSlotFilter] = useState("all");
  const [checkFilter, setCheckFilter] = useState("all");

  const rows = useMemo(() => {
    if (!activity) return [];
    const list = [];
    tickets.forEach((t) => t.bookings.forEach((b) => {
      if (b.activityId !== activity.id || b.status !== "Confirmed") return;
      const slot = getSlot(activity.id, b.slotId);
      list.push({
        ref: b.ref, ticketCode: t.ticketCode, parent: t.parentName, child: t.childName,
        age: t.childAge, mobile: t.mobile, email: t.email,
        slot: slot ? `${slot.start}–${slot.end}` : "-", slotId: b.slotId,
        status: b.status, checkIn: b.checkIn, date: t.purchaseDate,
      });
    }));
    return list;
  }, [tickets, activity, getSlot]);

  const filtered = rows.filter((r) => {
    if (slotFilter !== "all" && r.slotId !== slotFilter) return false;
    if (checkFilter !== "all" && r.checkIn !== checkFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!(r.ticketCode.toLowerCase().includes(s) || r.parent.toLowerCase().includes(s) ||
        r.child.toLowerCase().includes(s) || r.mobile.includes(s) || r.email.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  if (!activity) return <Navigate to="/admin/activities" replace />;

  const doExport = () => {
    exportCSV(`${activity.slug}-participants.csv`, filtered.map((r) => ({
      BookingRef: r.ref, TicketCode: r.ticketCode, Parent: r.parent, Child: r.child, Age: r.age,
      Mobile: r.mobile, Email: r.email, TimeSlot: r.slot, BookingStatus: r.status, CheckIn: r.checkIn, BookingDate: r.date,
    })));
    toast.success("Daftar peserta diekspor ke CSV.");
  };

  return (
    <div>
      <button onClick={() => navigate("/admin/activities")} className="flex items-center gap-1 text-slate-500 font-bold mb-3" data-testid="participants-back">
        <ChevronLeft className="w-4 h-4" /> Activities
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-slate-800">{activity.name} — Participants</h1>
          <p className="text-slate-500 font-semibold">{filtered.length} peserta terdaftar</p>
        </div>
        <button onClick={doExport} className="flex items-center gap-2 bg-grass text-white font-display rounded-lg px-4 py-2.5" data-testid="export-participants">
          <Download className="w-5 h-5" /> Export CSV
        </button>
      </div>

      {/* filters */}
      <div className="mt-5 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, kode tiket, no HP..."
            data-testid="participants-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-sea outline-none font-semibold text-slate-700" />
        </div>
        <select value={slotFilter} onChange={(e) => setSlotFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2.5 font-semibold text-slate-600" data-testid="filter-slot">
          <option value="all">Semua Slot</option>
          {activity.slots.map((s) => <option key={s.id} value={s.id}>{`${s.start}–${s.end}`}</option>)}
        </select>
        <select value={checkFilter} onChange={(e) => setCheckFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2.5 font-semibold text-slate-600" data-testid="filter-checkin">
          <option value="all">Semua Check-In</option>
          <option>Registered</option>
          <option>Checked In</option>
          <option>No Show</option>
        </select>
      </div>

      {/* table */}
      <div className="mt-5 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto thin-scroll">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-slate-500 font-bold text-left">
            <tr>
              {["Ref", "Ticket", "Parent", "Child", "Age", "Mobile", "Slot", "Check-In", "Aksi"].map((h) => (
                <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <tr key={r.ref} className="hover:bg-slate-50/60" data-testid={`prow-${r.ref}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.ref}</td>
                <td className="px-4 py-3 font-display text-coral">{r.ticketCode}</td>
                <td className="px-4 py-3 font-semibold text-slate-700">{r.parent}</td>
                <td className="px-4 py-3 text-slate-600">{r.child}</td>
                <td className="px-4 py-3 text-slate-600">{r.age}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.mobile}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.slot}</td>
                <td className="px-4 py-3"><StatusBadge status={r.checkIn} /></td>
                <td className="px-4 py-3">
                  <button
                    disabled={r.checkIn === "Checked In"}
                    onClick={() => { setCheckIn(r.ticketCode, r.ref, "Checked In"); toast.success(`${r.child} checked in!`); }}
                    className={cn("flex items-center gap-1 px-3 py-1.5 rounded-lg font-semibold text-xs",
                      r.checkIn === "Checked In" ? "bg-grass/15 text-grass" : "bg-sea text-white hover:bg-sea-deep")}
                    data-testid={`checkin-${r.ref}`}
                  >
                    <Check className="w-4 h-4" /> {r.checkIn === "Checked In" ? "Done" : "Check In"}
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400 font-semibold">Belum ada peserta.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
